import { ManhattanOneConnectionSettings } from './service.connection';

// Imports on BGIS UAT have taken from 17 minutes to 1.5 hours to report a result
export const MANHATTAN_ONE_RESULT_WAIT_DEFAULT_MINUTES = 120;

// The first result check is 2 minutes after the push; anything shorter fails documents Manhattan has not picked up yet
export const MANHATTAN_ONE_RESULT_WAIT_MIN_MINUTES = 5;

// A document left waiting longer than a day is better failed and checked in Manhattan by hand
export const MANHATTAN_ONE_RESULT_WAIT_MAX_MINUTES = 24 * 60;

/** The waits offered on a linked company's Documents tab */
export const MANHATTAN_ONE_RESULT_WAIT_OPTIONS_MINUTES = [15, 30, 45, 60, 90, 120];

// Minutes after the push at which the result is read: often while a quick import is likely, then thinning out
const CHECKPOINT_MINUTES = [2, 5, 10, 20, 30, 45, 60, 90, 120];
const LATE_CHECK_GAP_MINUTES = 30;

/** How long an invoice or bill waits for Manhattan's import result before it is failed: the company's setting, or the default when unset */
export function manhattanOneResultWaitMinutes(settings: Pick<Partial<ManhattanOneConnectionSettings>, 'resultWaitMinutes'> | null | undefined): number {

    const minutes = settings?.resultWaitMinutes;
    if (!Number.isInteger(minutes)) return MANHATTAN_ONE_RESULT_WAIT_DEFAULT_MINUTES;
    return Math.min(Math.max(minutes, MANHATTAN_ONE_RESULT_WAIT_MIN_MINUTES), MANHATTAN_ONE_RESULT_WAIT_MAX_MINUTES);
}

/** Minutes between result checks, the first counted from the push. The last check lands exactly on the wait. */
export function manhattanOneResultCheckGapsMinutes(waitMinutes: number): number[] {

    const checkpoints = CHECKPOINT_MINUTES.filter(minutes => minutes < waitMinutes);
    let last = checkpoints[checkpoints.length - 1] ?? 0;
    while (last + LATE_CHECK_GAP_MINUTES < waitMinutes) {
        last += LATE_CHECK_GAP_MINUTES;
        checkpoints.push(last);
    }
    checkpoints.push(waitMinutes);

    return checkpoints.map((minutes, index) => minutes - (checkpoints[index - 1] ?? 0));
}

/**
 * When to read an awaited import's result next: the first checkpoint after now, the last landing on giveUpAt.
 * Null once giveUpAt has passed, so the check running now is the last and must settle the document.
 * Counted from the stored submission rather than from how many checks ran, so a check that was delayed or
 * retried does not push the rest of the schedule back.
 */
export function manhattanOneNextResultCheckAt(submittedAt: Date | undefined, giveUpAt: Date | undefined, now: Date): Date | null {

    if (!giveUpAt || now.getTime() >= new Date(giveUpAt).getTime()) return null;

    const end = new Date(giveUpAt).getTime();
    const start = submittedAt ? new Date(submittedAt).getTime() : end;
    let checkpoint = start;
    for (const gap of manhattanOneResultCheckGapsMinutes(Math.round((end - start) / 60_000))) {
        checkpoint += gap * 60_000;
        if (checkpoint > now.getTime()) return new Date(Math.min(checkpoint, end));
    }

    return new Date(end);
}

/** "2 hours", "1.5 hours", "1 hour", "45 minutes" */
export function describeManhattanOneResultWait(minutes: number): string {

    if (minutes < 60 || minutes % 30 !== 0) return `${minutes} minutes`;
    const hours = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
}

/** The offered wait closest to a stored one, so a wait saved before the options existed still shows */
export function nearestManhattanOneResultWaitOption(minutes: number): number {

    return MANHATTAN_ONE_RESULT_WAIT_OPTIONS_MINUTES.reduce((best, option) => Math.abs(option - minutes) < Math.abs(best - minutes) ? option : best);
}
